import React, { useEffect, useState } from 'react';
import { PublicHeader } from '../components/PublicHeader';
import { motion } from 'framer-motion';
import { Instagram, Youtube, Music, Heart, Star, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AboutPage() {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/about`);
      setAboutData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setAboutData(getDefaultAboutData());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultAboutData = () => ({
    title: 'Sobre Beto Carvalho',
    subtitle: 'Apaixonado por Conhecimentos Gerais',
    bio: 'Olá! Sou o Beto Carvalho, criador de conteúdo educacional apaixonado por compartilhar conhecimentos gerais de forma divertida e acessível. Acredito que aprender não precisa ser chato, e por isso criei esta plataforma para tornar o aprendizado mais interativo e engajador.\n\nCom anos de experiência criando conteúdo nas redes sociais, decidi levar minha paixão por educar para o próximo nível, criando uma plataforma completa onde você pode aprender, jogar e se divertir ao mesmo tempo.',
    mission: 'Tornar o conhecimento geral acessível e divertido para todos, criando uma comunidade de aprendizado contínuo.',
    social_stats: {
      instagram: { followers: '50K+', engagement: 'Alto' },
      youtube: { subscribers: '100K+', views: '5M+' },
      tiktok: { followers: '80K+', likes: '2M+' }
    },
    highlights: [
      'Criador de conteúdo educacional desde 2020',
      'Mais de 500 vídeos publicados',
      'Comunidade de 200K+ seguidores',
      'Especialista em conhecimentos gerais'
    ]
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  const data = aboutData || getDefaultAboutData();

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {data.title}
            </h1>
            <p className="text-xl text-muted-foreground">{data.subtitle}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-foreground mb-6 leading-relaxed">
                {data.bio}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16 p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <Heart className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Minha Missão</h2>
            </div>
            <p className="text-lg text-foreground">{data.mission}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Me siga nas redes sociais
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a
                href="https://www.instagram.com/beto_carvalho_ofc/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-2xl bg-white border border-border hover:shadow-lg transition-all group"
              >
                <Instagram className="h-12 w-12 mb-4 text-pink-600 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">Instagram</h3>
                <p className="text-muted-foreground mb-2">{data.social_stats.instagram.followers} seguidores</p>
                <p className="text-sm text-primary">Engajamento {data.social_stats.instagram.engagement}</p>
              </a>

              <a
                href="https://www.youtube.com/@beto_carvalho_ofc"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-2xl bg-white border border-border hover:shadow-lg transition-all group"
              >
                <Youtube className="h-12 w-12 mb-4 text-red-600 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">YouTube</h3>
                <p className="text-muted-foreground mb-2">{data.social_stats.youtube.subscribers} inscritos</p>
                <p className="text-sm text-primary">{data.social_stats.youtube.views} visualizações</p>
              </a>

              <a
                href="https://www.tiktok.com/@beto_carvalho_ofc"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-2xl bg-white border border-border hover:shadow-lg transition-all group"
              >
                <Music className="h-12 w-12 mb-4 text-black group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">TikTok</h3>
                <p className="text-muted-foreground mb-2">{data.social_stats.tiktok.followers} seguidores</p>
                <p className="text-sm text-primary">{data.social_stats.tiktok.likes} curtidas</p>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Destaques
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.highlights && data.highlights[0] && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                  <Star className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-foreground">{data.highlights[0]}</span>
                </div>
              )}
              {data.highlights && data.highlights[1] && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                  <Star className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-foreground">{data.highlights[1]}</span>
                </div>
              )}
              {data.highlights && data.highlights[2] && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                  <Star className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-foreground">{data.highlights[2]}</span>
                </div>
              )}
              {data.highlights && data.highlights[3] && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                  <Star className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <span className="text-foreground">{data.highlights[3]}</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
